import {
  ENVIRONMENT,
  RELEASE,
  REPLAY_ENABLED,
  REPLAY_SESSION_SAMPLE_RATE,
} from './config'
import { buildStateSummary } from './stateSummary'
import { createEventBudget } from './eventBudget'

/**
 * Public surface of the observability harness. This is the ONLY module the rest
 * of the app may import, and `@sentry/react` is imported nowhere else.
 *
 * There is deliberately no exported `captureError()`. If feature code could
 * call a reporting function, feature code would contain reporting-shaped
 * residue that has to be unpicked at launch. Instead the harness attaches
 * itself to seams the app already has: the `app-error` CustomEvent from
 * AppErrorBoundary, an extra axios interceptor, `store.subscribe`, and
 * `window.onerror`. The feedback form is the Sentry integration's own; the app
 * only opens it from the user menu via `openFeedbackForm()` (no floating
 * button - it sat on top of page content).
 *
 * See README.md for the removal procedure.
 */

/** Resolves to the Sentry namespace once loaded, or null when disabled. */
let sentryPromise = null

/**
 * The Redux store, captured during init so the event processor can read state
 * synchronously. Held here rather than statically imported so this module stays
 * cheap to load; the dependency points inward (observability -> app), the same
 * direction `src/api/client.js` already takes.
 */
let storeRef = null

/**
 * Whether the harness is active in this build.
 *
 * @returns {boolean}
 */
export function isObservabilityEnabled() {
  return Boolean(import.meta.env.VITE_SENTRY_DSN)
}

/**
 * Internal: resolves to the loaded Sentry namespace, or null when disabled or
 * the load failed. Exposed for tests; app code must not use it.
 *
 * @returns {Promise<object|null>}
 */
export function getSentry() {
  return sentryPromise || Promise.resolve(null)
}

/**
 * Start error capture. Safe to call once, at boot, without awaiting — nothing
 * about app startup should depend on this.
 *
 * @returns {Promise<void>}
 */
export async function initObservability() {
  // This guard MUST be a literal `import.meta.env` test in this function, next
  // to the dynamic import. Vite substitutes the value at transform time, so
  // with no DSN this becomes `if (!undefined) return` and Rollup proves the
  // rest unreachable — dropping the import() and emitting no Sentry chunk at
  // all. Routing it through config.js or a helper breaks that.
  if (!import.meta.env.VITE_SENTRY_DSN) return

  if (sentryPromise) return // idempotent

  sentryPromise = import('@sentry/react')
    .then(async (Sentry) => {
      storeRef = (await import('@/store')).store
      const { watchAuthContext } = await import('./context')
      const { watchApiErrors, getRecentApiFailures } = await import('./apiErrors')
      const { watchRoute, currentRouteTags } = await import('./routeTag')
      const { watchAppErrors } = await import('./appErrors')
      const { isolateFeedbackWidget } = await import('./widgetIsolation')

      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: ENVIRONMENT,
        release: RELEASE,

        // Tracing and profiling are the cost drivers and buy nothing for
        // triage; this harness only needs errors and feedback.
        tracesSampleRate: 0,
        profilesSampleRate: 0,

        // Give plain captureMessage calls (including feedback) a stack.
        attachStacktrace: true,

        // Repeats of one error stop after a few sends; see
        // eventBudget.js. Errors only: the SDK never passes feedback here.
        beforeSend: createEventBudget(),

        // Browser-extension frames are never Graphit code.
        denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari(-web)?-extension:\/\//],

        integrations: [
          // MUST be registered explicitly: feedbackIntegration is NOT one of
          // the SDK's default integrations, so without this getFeedback()
          // returns undefined and the form can never open.
          // autoInject is OFF: the SDK's floating button covered page content
          // (dialog footers, table pagination). The form is opened on demand
          // from the user menu instead - see `openFeedbackForm()`.
          Sentry.feedbackIntegration({
            autoInject: false,
            // Note: the option is `triggerLabel`, not `buttonLabel`. Unknown
            // keys are silently ignored, so a typo here fails quietly.
            triggerLabel: 'Provide feedback',
            triggerAriaLabel: 'Provide feedback',
            formTitle: 'Provide feedback',
            submitButtonLabel: 'Send report',
            messageLabel: 'Please write your feedback here',
            messagePlaceholder:
              'What were you doing, what did you expect to happen, and what happened instead? Steps to reproduce help a lot.',
            successMessageText: 'Thanks — this went straight to the dev team.',
            // Identity comes from the auth scope (see context.js), so the
            // tester doesn't retype it.
            showName: false,
            showEmail: false,
            // Native Screen Capture API: pixel-accurate, unaffected by this
            // app's oklch()/color-mix() CSS, and it captures canvas charts.
            // The SDK hides the button on mobile, where it isn't supported.
            enableScreenshot: true,
            showBranding: false,
            // Match the app rather than following the OS, which would render a
            // dark widget over a light UI.
            colorScheme: 'light',
          }),
          // No options: the SDK's own privacy defaults (maskAllText,
          // maskAllInputs and blockAllMedia all true) apply. Live tenants'
          // names and giving figures must never reach a replay as text.
          ...(REPLAY_ENABLED ? [Sentry.replayIntegration()] : []),
        ],
        // Errored sessions are always worth the quota. Recording *every* session
        // is the dial, since each one spends a replay: see config.js.
        replaysOnErrorSampleRate: REPLAY_ENABLED ? 1.0 : 0,
        replaysSessionSampleRate: REPLAY_ENABLED ? REPLAY_SESSION_SAMPLE_RATE : 0,
      })

      // Enrichment lives in an event processor, NOT in `beforeSend`.
      //
      // `beforeSend` only runs for error events: the SDK gates it on
      // `isErrorEvent()`, which tests `event.type === undefined`. A feedback
      // submission is `{ type: 'feedback' }`, so `beforeSend` never sees it —
      // and a hand-written UI report is exactly the case that needs this
      // context most. Event processors run via `prepareEvent` for every event
      // type, so both errors and feedback get the same attachments.
      Sentry.addEventProcessor((event) => {
        // A throw here rejects the whole event, which looks exactly like a
        // misconfigured DSN. Never let enrichment lose the report.
        try {
          event.contexts = {
            ...event.contexts,
            api_failures: { recent: getRecentApiFailures() },
            redux: buildStateSummary(getStoreState()),
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
              dpr: window.devicePixelRatio,
            },
          }
          event.tags = { ...currentRouteTags(), ...event.tags }
        } catch (error) {
          console.warn('[observability] failed to enrich event', error)
        }
        return event
      })

      watchAuthContext(Sentry)
      watchApiErrors(Sentry)
      watchRoute()
      watchAppErrors(Sentry)
      // Keeps the widget usable while a Radix modal is open.
      isolateFeedbackWidget()

      return Sentry
    })
    .catch((error) => {
      // A failed telemetry load must never take the app down with it.
      console.warn('[observability] disabled: failed to load', error)
      return null
    })

  await sentryPromise
}

/**
 * Open the Sentry feedback form (the same one the SDK's floating button
 * opened). Resolves to false when the harness is disabled, still loading, or
 * the integration is missing, so callers can no-op silently.
 *
 * The SDK creates the form's shadow host (`#sentry-feedback`) on first use;
 * `isolateFeedbackWidget()` is already watching for it, so the form keeps
 * working over an open Radix dialog.
 *
 * @returns {Promise<boolean>} Whether the form was opened
 */
export async function openFeedbackForm() {
  const Sentry = await getSentry()
  const feedback = Sentry?.getFeedback?.()
  if (!feedback) return false
  try {
    const form = await feedback.createForm()
    form.appendToDom()
    form.open()
    return true
  } catch (error) {
    console.warn('[observability] failed to open the feedback form', error)
    return false
  }
}

/**
 * Current Redux state, or null before init completes.
 *
 * @returns {Object|null}
 */
function getStoreState() {
  return storeRef?.getState?.() ?? null
}
